import { Controller } from "@hotwired/stimulus"

// Shows the real PDF of a free tool: posts the form to the preview endpoint after a short pause
// and displays the response through a blob URL. Nothing is stored on the server.
export default class extends Controller {
  static targets = ["frame", "status", "link", "errors", "errorTitle", "errorList"]
  static values = { url: String, token: String, delay: { type: Number, default: 600 }, failedMessage: String }

  connect() {
    this.invalidTitle = this.errorTitleTarget.textContent
    this.timer = setTimeout(() => this.refresh(), 0)
  }

  disconnect() {
    clearTimeout(this.timer)
    this.request?.abort()
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
  }

  schedule() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.refresh(), this.delayValue)
  }

  async refresh() {
    this.request?.abort()
    const request = this.request = new AbortController()
    this.setLoading(true)

    // The form's own token is bound to the download action (per-form CSRF tokens), so the preview uses its own.
    const body = new FormData(this.element)
    body.set("authenticity_token", this.tokenValue)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body,
        headers: { Accept: "application/pdf, application/json" },
        credentials: "same-origin",
        signal: request.signal
      })

      if (response.ok && response.headers.get("Content-Type")?.startsWith("application/pdf")) {
        this.showPdf(await response.blob())
      } else if (response.status === 422) {
        const { errors } = await response.json()
        this.showErrors(this.invalidTitle, errors)
      } else {
        this.showErrors(this.failedMessageValue, [])
      }
    } catch (error) {
      if (error.name !== "AbortError") this.showErrors(this.failedMessageValue, [])
    } finally {
      if (this.request === request) this.setLoading(false)
    }
  }

  showPdf(blob) {
    const previous = this.objectUrl
    this.objectUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }))
    this.frameTarget.src = `${this.objectUrl}#toolbar=0&navpanes=0&view=FitH`
    this.linkTarget.href = this.objectUrl
    this.linkTarget.hidden = false
    this.errorsTarget.hidden = true
    if (previous) URL.revokeObjectURL(previous)
  }

  showErrors(title, messages) {
    this.errorTitleTarget.textContent = title
    this.errorListTarget.replaceChildren(...messages.map((message) => {
      const item = document.createElement("li")
      item.textContent = message
      return item
    }))
    this.errorListTarget.hidden = messages.length === 0
    this.errorsTarget.hidden = false
  }

  setLoading(loading) {
    this.statusTarget.hidden = !loading
    this.frameTarget.classList.toggle("opacity-60", loading)
    this.frameTarget.setAttribute("aria-busy", loading)
  }
}
